import { ThemeKey } from "@smartsheet/design-tokens";
import { color } from "@smartsheet/design-tokens-react";
import { DeleteIcon } from "@smartsheet/lodestar-icons";
import { themed } from "@smartsheet/sds-theme-internal";
import { CloseIcon } from "@smartsheet/ursa-icons";
import React, { useCallback } from "react";
import { forwardRef } from "react";
import { useTheme } from "../../theme/utils/useTheme";
import { focusRingTheme } from "../../util/createFocusRingStyles";
import { InputFieldBaseProps, getInputFieldHeightValue } from "../../util/inputFieldBaseProps";
import { Clickable, ClickableProps } from "../clickable";
import { TextInputProps } from "./TextInput";
import {
    StyledButton,
    StyledLodestarSearchIcon,
    StyledTextInput,
    StyledUrsaSearchIcon,
} from "./styled/StyledSearchInputComponents";
import { formInputTheme } from "./styled/formInputTheme";

const searchIcon = themed({
    rest: {
        "lodestar-light": <StyledLodestarSearchIcon size="small" color={color("color--icons--neutral--stronger")} />,
        "ursa-light": <StyledUrsaSearchIcon size={24} color={color("color--text--neutral--weaker")} />,
    },
    hover: {
        "lodestar-light": <StyledLodestarSearchIcon size="small" color={color("color--icons--neutral--strongest")} />,
        "ursa-light": <StyledUrsaSearchIcon size={24} color={color("color--text--neutral--weak")} />,
    },
});

const clearIcon = themed({
    rest: {
        "lodestar-light": <DeleteIcon size="small" color={color("color--icons--neutral--stronger")} />,
        "ursa-light": <CloseIcon size={20} color={color("color--text--neutral--weaker")} />,
    },
    hover: {
        "lodestar-light": <DeleteIcon size="small" color={color("color--icons--neutral--strongest")} />,
        "ursa-light": <CloseIcon size={20} color={color("color--text--neutral--weak")} />,
    },
});

// Reserving namespace for consistency.
export interface SearchInputProps extends TextInputProps {
    /**
     * Optional props to merge with the "x" icon
     */
    contentAfterButtonProps?: {
        onClick: React.ButtonHTMLAttributes<HTMLButtonElement>["onClick"];
    };
    /**
     * Optional props to make the search icon clickable. By using this you must define an
     * aria-label as well to explain to users what clicking on the button will do.
     */
    contentBeforeButtonProps?: Omit<ClickableProps, "children">;
}

interface ClearSearchButtonProps extends InputFieldBaseProps {
    isDisabled: boolean;
    isHovered: boolean;
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
    value: string;
}
const ClearSearchButton = ({ inputFieldHeight, isDisabled, isHovered, onClick, value }: ClearSearchButtonProps) => {
    const { key } = useTheme();
    const themeMode = key as ThemeKey;
    const onClickedCallback = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            if (isDisabled) {
                return;
            }

            onClick(e);
        },
        [isDisabled, onClick]
    );

    if (value === "") {
        return null;
    }

    const IconComponent = isHovered ? clearIcon.hover(themeMode) : clearIcon.rest(themeMode);

    return (
        <StyledButton
            inputFieldHeight={inputFieldHeight}
            isDisabled={isDisabled}
            aria-label="clear"
            type="button"
            themeMode={themeMode}
            onClick={onClickedCallback}
        >
            {IconComponent}
        </StyledButton>
    );
};

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>((props, parentRef) => {
    const { key } = useTheme();
    const themeMode = key as ThemeKey;
    const {
        inputFieldHeight: inputFieldHeightProp,
        contentAfterButtonProps,
        contentBeforeButtonProps,
        isDisabled = false,
        onChange,
        value,
    } = props;

    const inputFieldHeight = getInputFieldHeightValue(themeMode, inputFieldHeightProp);
    const contentAfterButtonClick = contentAfterButtonProps?.onClick;

    return (
        <StyledTextInput
            type="search"
            contentBefore={({ isHovered }) => {
                const icon = isHovered ? searchIcon.hover(themeMode) : searchIcon.rest(themeMode);

                if (contentBeforeButtonProps !== undefined) {
                    return (
                        <Clickable
                            {...contentBeforeButtonProps}
                            focusRingOptions={{
                                colorOverride: focusRingTheme.outerColor(themeMode),
                                radius: formInputTheme.searchInputButtonRadius(themeMode),
                            }}
                        >
                            {icon}
                        </Clickable>
                    );
                } else {
                    return icon;
                }
            }}
            contentAfter={({ isHovered }) => (
                <ClearSearchButton
                    inputFieldHeight={inputFieldHeight}
                    value={value}
                    isDisabled={isDisabled}
                    isHovered={isHovered}
                    onClick={(e) => {
                        onChange("", e);

                        if (contentAfterButtonClick != null) {
                            contentAfterButtonClick(e);
                        }
                    }}
                />
            )}
            ref={parentRef}
            {...props}
        />
    );
});

SearchInput.displayName = "SearchInput";
